import { request } from '@/utils/request';

export type SecuritiesSummary = {
  accountNumber: string;
  netAssetValue: number;
  accounts: Array<{
    name: string;
    label: string;
    lastUpdatedAt: Date;
    value: number;
  }>;
};

export type SecuritiesAccount = {
  id: string;
  brokerId: string;
  name: string;
  label: string;
  accountNumber: string;
  logoUrl: string;
  websiteUrl: string;
  lastUpdatedAt: Date;
  totalValue: number;
  totalValueUsd: number;
  investmentValue: number;
  cashValue: number;
  currency: string;
  cashBalances?: Array<{
    currency: string;
    balance: number;
  }>;
  cashCurrencies?: string[];
};

export type SecurityProduct = {
  account: {
    brokerName: string;
    accountNumber: string;
  };
  productName: string;
  productCode: string;
  quantity: number;
  value: number;
  currency: string;
  pnl: number;
};

export async function querySecuritiesSummary(params: { userId: string }): Promise<SecuritiesSummary> {
  return request<SecuritiesSummary>(`/securities/summary`, {
    params: params
  });
}

export type QuerySecuritiesAccountsParams = {
  userId: string;
  label?: string;
  securitiesBroker?: string;
  currency?: string;
  accountNumber?: string;
  current?: number;
  pageSize?: number;
  vendorId?: string;
  rmId?: string;
  sortField?: string;
  sortOrder?: 'ascend' | 'descend';
};

type SecuritiesAccountsResponse =
  | SecuritiesAccount[]
  | {
      data?: SecuritiesAccount[];
    };

export async function querySecuritiesAccounts(params: QuerySecuritiesAccountsParams): Promise<SecuritiesAccount[]> {
  const response = await request<SecuritiesAccountsResponse>(`/securities/accounts`, {
    params: params
  });

  if (Array.isArray(response)) {
    return response;
  }

  return response.data ?? [];
}

export async function querySecurityProducts(params: { userId: string; productType: string }): Promise<SecurityProduct[]> {
  return request<SecurityProduct[]>(`/securities/products`, {
    params: params
  });
}

export type PortfolioBalanceData = {
  cashValue: number;
  investmentValue: number;
  currency: string;
};

export type PortfolioCashPosition = {
  amount: number;
  currency: string;
};

export type PortfolioProductItem = {
  id?: string;
  name: string;
  productCode: string;
  symbol: string;
  quantity: number;
  value: number;
  currency: string;
  productType: string;
};

export type SecuritiesPortfolioData = {
  balance: PortfolioBalanceData;
  cashPositions: PortfolioCashPosition[];
  product: PortfolioProductItem[];
};

export async function querySecuritiesPortfolio(params: {
  brokerId: string;
  userId: string;
  accountNumber: string;
  currency: string;
}): Promise<SecuritiesPortfolioData> {
  return request<SecuritiesPortfolioData>(`/securities/manual-last-update`, {
    params,
  });
}

export type SecuritiesTransferItem = {
  id: string;
  date: Date;
  refId: string;
  type: string;
  amount: number;
  currency: string;
  status: string;
  securitiesAccount: string;
  user: {
    email: string;
    id: string;
    name: string;
  };
  securitiesBroker: {
    id: string;
    label: string;
    name: string;
  };
};

/**
 * Get pending securities transfers count
 * Filters by status: NEW, PENDING_APPROVE, PENDING_FUNDING
 */
export async function querySecuritiesTransfersPendingCount(): Promise<number> {
  const response = await request<{ data: SecuritiesTransferItem[]; total: number }>(`/securities/transfers`, {
    params: {
      current: 1,
      pageSize: 1,
      status: 'NEW'
    }
  });
  return response.total ?? 0;
}
